/**
 * @file   src\Pages\UserVisitDetailsReport.tsx
 * @brief  User Visit Details report page.
 * @date   JAN, 2026
 * @author ZCO Engineer
 * @copyright (c) 2026, ZCO
 */

import {
  Container,
  Table,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  useState,
  Button,
  useParams,
  Pagination,
  React,
} from "../utils/thirdpartyComponents";
import { userType } from "../utils/enums";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, RevenueApi } from "../utils/apiUrls";
import SmallLoader from "../components/SmallLoader";
import CWSearchinput from "../components/CWSearchinput";
import { MessageToaster } from "../utils/toastUtil";
import DatePicker from "react-datepicker";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

interface UserVisitRecord {
  UserName?: string;
  Phone?: string;
  Location?: string;
  DateOfVisit?: string;
  TimeOfVisit?: string;
  BayUsed?: string;
  WashName?: string;
  LicensePlateNumber?: string;
}

const UserVisitDetailsReport = (props: any) => {
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;
  const navigate = useNavigate();
  const params = useParams();
  const toaster = new MessageToaster();
  const timezoneOffset = new Date().getTimezoneOffset();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [records, setRecords] = useState<UserVisitRecord[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    searchText: "",
  });

  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const pageSize = 10;

  useEffect(() => {
    getCompany();
    getCompanyAddress();
  }, []);

  useEffect(() => {
    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (hasSearched) {
      getUserVisitDetails(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getCompany = async () => {
    if (carwash_owner) return;
    try {
      if (!params.id) return;
      await axios.get(CompanyApi.GET_COMPANY + `${params.id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const handleUpdate = () => {
    updateData({
      heading: "User Visit Details",
      url: carwash_owner
        ? ""
        : `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton: props.userType === userType["CARWASH-OWNER"] ? false : true,
      showMainMenu: true,
      activeLink:
        props.userType === userType["CARWASH-OWNER"]
          ? "reports"
          : "manage-car-wash",
    });
  };

  const handleFromDateChange = (date: Date | null) => {
    setFromDate(date);
    if (date) {
      setFilters((prev) => ({
        ...prev,
        fromDate: moment(date).format("YYYY-MM-DD"),
      }));
    } else {
      setFilters((prev) => ({ ...prev, fromDate: "" }));
    }
  };

  const handleToDateChange = (date: Date | null) => {
    setToDate(date);
    if (date) {
      setFilters((prev) => ({
        ...prev,
        toDate: moment(date).format("YYYY-MM-DD"),
      }));
    } else {
      setFilters((prev) => ({ ...prev, toDate: "" }));
    }
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange("searchText", event.target.value);
  };

  const handleClearSearch = () => {
    handleFilterChange("searchText", "");
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return moment(dateString).format("MM/DD/YYYY");
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return "";
    return moment(timeString, ["HH:mm:ss", "HH:mm"]).format("h:mm A");
  };

  const getUserVisitDetails = async (pageNo = 1) => {
    if (!filters.fromDate || !filters.toDate || !filters.searchText) {
      toaster.toastError("Select date range and enter search text.");
      return;
    }
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("offset", timezoneOffset.toString());
      queryParams.append("pageNo", pageNo.toString());
      queryParams.append("pageSize", pageSize.toString());
      queryParams.append("fromDate", filters.fromDate);
      queryParams.append("toDate", filters.toDate);
      queryParams.append("searchText", filters.searchText);

      const apiUrl = RevenueApi.CARWASH_OWNER_USER_VISIT_DETAILS_REPORT;
      const { data } = await axios.get(`${apiUrl}?${queryParams.toString()}`);

      if (data?.error === 0) {
        const list = data?.data?.list ?? data?.data ?? [];
        const totalCount = data?.data?.totalCount ?? list.length;
        const limit = data?.data?.limit ?? pageSize;
        const mapped = (Array.isArray(list) ? list : []).map((item: any) => ({
          UserName: item?.UserName ?? item?.FullName ?? "",
          Phone: item?.Phone ?? item?.PhoneNumber ?? "",
          Location: item?.Location ?? item?.LocationName ?? "",
          DateOfVisit: item?.DateOfVisit ?? item?.VisitDate ?? item?.VisitDateTime ?? "",
          TimeOfVisit: item?.TimeOfVisit ?? item?.VisitTime ?? item?.VisitDateTime ?? "",
          BayUsed: item?.BayUsed ?? item?.BayName ?? "",
          WashName: item?.WashName ?? "",
          LicensePlateNumber:
            item?.LicensePlateNumber ?? item?.licensePlateNumber ?? "",
        }));
        setRecords(mapped);
        setTotalPages(Math.ceil(totalCount / limit) || 1);
      } else {
        setRecords([]);
      }
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || error?.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadReportAsExcel = async () => {
    if (!filters.fromDate || !filters.toDate || !filters.searchText) {
      toaster.toastError("Select date range and enter search text.");
      return;
    }
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("offset", timezoneOffset.toString());
      queryParams.append("pageNo", "1");
      queryParams.append("pageSize", "10000");
      queryParams.append("fromDate", filters.fromDate);
      queryParams.append("toDate", filters.toDate);
      queryParams.append("searchText", filters.searchText);

      const apiUrl = RevenueApi.CARWASH_OWNER_USER_VISIT_DETAILS_REPORT;
      const { data } = await axios.get(`${apiUrl}?${queryParams.toString()}`);

      if (data?.error !== 0) {
        toaster.toastError(data?.message || "Failed to download report.");
        return;
      }

      const list = data?.data?.list ?? data?.data ?? [];
      const rows = (Array.isArray(list) ? list : []).map((item: any) => ({
        "User Name": item?.UserName ?? item?.FullName ?? "",
        Phone: item?.Phone ?? item?.PhoneNumber ?? "",
        "Location": item?.Location ?? item?.LocationName ?? "",
        "Date of Visit": formatDate(item?.DateOfVisit ?? item?.VisitDate ?? item?.VisitDateTime ?? ""),
        "Time of Visit": formatTime(item?.TimeOfVisit ?? item?.VisitTime ?? item?.VisitDateTime ?? ""),
        "Bay Used": item?.BayUsed ?? item?.BayName ?? "",
        "Wash Name": item?.WashName ?? "",
        "License Plate": item?.LicensePlateNumber ?? item?.licensePlateNumber ?? "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "UserVisitDetails");
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileSearch = (filters.searchText || "")
        .toString()
        .trim();
      const fileName = `UserVisitDetails_${fileSearch}_${filters.fromDate}_${filters.toDate}.xlsx`;
      saveAs(blob, fileName);
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || error?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <div className="manage-carwash-container manage-subscription-container">
      <Container>
        <Breadcrumb>
          <Breadcrumb.Item
            onClick={() => {
              navigate(
                carwash_owner
                  ? `/${pageURLs.dashboard}`
                  : `/superadmin/${pageURLs.dashboard}`
              );
            }}
          >
            Dashboard
          </Breadcrumb.Item>
          {carwash_owner ? null : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  navigate(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  navigate(`/superadmin/${pageURLs.carwash_locations}/${params.id}`);
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}
          <Breadcrumb.Item active>User Visit Details</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
              <div className="d-flex flex-column">
                <label className="form-label">Date Range</label>
                <div className="d-flex align-items-center">
                  <DatePicker
                    showIcon
                    selected={fromDate}
                    onChange={handleFromDateChange}
                    placeholderText="From Date"
                    dateFormat="MM/dd/yyyy"
                  />
                  <div className="ms-3">
                    <DatePicker
                      showIcon
                      selected={toDate}
                      onChange={handleToDateChange}
                      placeholderText="To Date"
                      minDate={fromDate || undefined}
                      dateFormat="MM/dd/yyyy"
                    />
                  </div>
                </div>
              </div>
              <div className="d-flex flex-column ms-3">
                <label className="form-label">Search License Plate or Phone Number</label>
                <CWSearchinput
                  id="searchText"
                  name="searchText"
                  type="text"
                  placeholder="Search by License Plate or Phone"
                  value={filters.searchText}
                  onChange={handleSearchChange}
                  onClose={handleClearSearch}
                />
              </div>
              <div className="button-group d-flex">
                <Button
                  className="primary-btn"
                  variant="primary"
                  title="Run Report"
                  onClick={() => {
                    if (!filters.fromDate || !filters.toDate || !filters.searchText) {
                      toaster.toastError("Select date range and enter search text.");
                      return;
                    }
                    setCurrentPage(1);
                    setHasSearched(true);
                    getUserVisitDetails(1);
                  }}
                >
                  Run Report
                </Button>
                <Button
                  className="secondary-btn ms-3"
                  variant="outline-primary"
                  title="Download Excel"
                  onClick={downloadReportAsExcel}
                >
                  Download Excel
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="cw-table-wrap report-table">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>User Name</th>
                <th>Phone</th>
                <th>Location</th>
                <th>Date of Visit</th>
                <th>Time of Visit</th>
                <th>Bay Used</th>
                <th>Wash Name</th>
                <th>License Plate</th>
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((item, index) => (
                  <tr key={index}>
                    <td>{item.UserName ?? ""}</td>
                    <td>{item.Phone ?? ""}</td>
                    <td>{item.Location ?? ""}</td>
                    <td>{formatDate(item.DateOfVisit ?? "")}</td>
                    <td>{formatTime(item.TimeOfVisit ?? "")}</td>
                    <td>{item.BayUsed ?? ""}</td>
                    <td>{item.WashName ?? ""}</td>
                    <td>{item.LicensePlateNumber ?? ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="text-center">
                    {loading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {records.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>
      {loading && <SmallLoader />}
    </div>
  );
};

export default UserVisitDetailsReport;

